import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import BomImport from './BomImport';
import BomControlledDetails from './BomControlledDetails';
import { canUseChangeControl } from '../changes/changeControl';

const API_BASE = process.env.REACT_APP_API_URL;
const ACCENT = '#f472b6';
const STATUS_COLORS = { Draft:'#94a3b8','In Review':'#60a5fa',Released:'#46BB89',Superseded:'#f59e0b',Obsolete:'#EC2124' };
const TRANSITIONS = {
  submit:  { from:'Draft',     to:'In Review', label:'Submit for Review' },
  revise:  { from:'Released',  to:'Draft',     label:'Revise / New Draft' },
  approve: { from:'In Review', to:'Released',  label:'Approve & Release' },
  reject:  { from:'In Review', to:'Draft',     label:'Reject' },
  obsolete:{ from:null,        to:'Obsolete',  label:'Mark Obsolete' },
};
const SUPPLIERS = ['gpv','ubiqconn','acmepoint','hk_ringer'];

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || '#666';
  return <span style={{ display:'inline-block',padding:'2px 10px',borderRadius:20,fontSize:11,
    fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',background:c+'22',color:c }}>{status}</span>;
}
function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}
const BLANK_ITEM = { itemNumber:'', ninPartNumber:'', revision:'', description:'', quantity:null, unit:'pcs',
  manufacturerPartNumber:'', manufacturer:'', reference:'', notes:'', attributes:[] };

export default function BomDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedRevision = searchParams.get('revision') || '';
  const [snapshot, setSnapshot] = useState(null);
  const [availableRevisions, setAvailableRevisions] = useState([]);
  const loadSequence = useRef(0);
  const { getToken } = useAuth(); const { user } = useUser(); const navigate = useNavigate();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canManage = ['admin','quality-manager','production-engineer','service'].includes(role);
  const supplierKey = SUPPLIERS.includes(String(role||'').replace(/^test-/,''));

  const [bom, setBom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [productFamily, setProductFamily] = useState('');
  const [productNumbersText, setProductNumbersText] = useState('');
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);

  // Save / transition modal state
  const [changeSummary, setChangeSummary] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [actionNote, setActionNote] = useState('');

  // Supplier visibility (NI governance — independent of the content revision)
  const [vis, setVis] = useState([]);
  const [draftVis, setDraftVis] = useState([]);
  const [visSaving, setVisSaving] = useState(false);

  // Supersede modal state
  const [superseding, setSuperseding] = useState(false);
  const [supItems, setSupItems] = useState([]);
  const [supName, setSupName] = useState('');
  const [supSaving, setSupSaving] = useState(false);
  const [supError, setSupError] = useState('');
  const [supFileName, setSupFileName] = useState('');

  const applyBom = useCallback((b) => {
    setBom(b);
    setName(b.name || '');
    setProductFamily(b.productFamily || '');
    setProductNumbersText((b.productNumbers || []).join('\n'));
    setItems((b.items || []).map(it => ({ ...BLANK_ITEM, ...it })));
    setVis(b.visibility?.suppliers || []);
    setDraftVis(b.visibility?.draftSuppliers || []);
    setDirty(false);
  }, []);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true); setError('');
    try {
      const token = await getToken();
      const query = requestedRevision ? `?revision=${encodeURIComponent(requestedRevision)}` : '';
      const res = await window.fetch(`${API_BASE}/quality/boms/${id}${query}`, { headers:{ Authorization:`Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || 'Could not load BOM');
      if (sequence === loadSequence.current) { applyBom(data.bom); setSnapshot(data.snapshot || null); setAvailableRevisions(data.availableRevisions || []); }
    } catch (err) { if (sequence === loadSequence.current) { setError(err.message); setBom(null); } } finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [getToken, id, applyBom, requestedRevision]);

  useEffect(() => { load(); }, [load]);

  const setItem = (i, k, v) => { setItems(arr => arr.map((it,j)=>j===i?{...it,[k]:v}:it)); setDirty(true); };
  const addItem = () => { setItems(arr => [...arr, { ...BLANK_ITEM }]); setDirty(true); };
  const removeItem = i => { setItems(arr => arr.filter((_,j)=>j!==i)); setDirty(true); };

  const save = async () => {
    if (requestedRevision || snapshot) return;
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const productNumbers = productNumbersText.split(/[\n,]/).map(s=>s.trim()).filter(Boolean);
      const cleanItems = items.map(it => ({
        ...it,
        quantity: (it.quantity === '' || it.quantity == null || !Number.isFinite(parseFloat(it.quantity))) ? null : parseFloat(it.quantity),
        unit: it.unit ?? '',
        attributes: it.attributes || [],
      }));
      const res = await window.fetch(`${API_BASE}/quality/boms/${id}`, {
        method:'PUT', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ name, productFamily, productNumbers, items: cleanItems, references: bom.references || [], openPoints: bom.openPoints || [], changeSummary, expectedVersion:bom.__v }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Save failed');
      applyBom(data.bom); setShowSaveModal(false); setChangeSummary('');
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleAction = async action => {
    if (requestedRevision || snapshot) return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/boms/${id}/submit`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ action, changeSummary: actionNote, expectedVersion:bom.__v }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Action failed');
      applyBom(data.bom); setActionModal(null); setActionNote('');
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const saveVisibility = async () => {
    setVisSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/boms/${id}/visibility`, {
        method:'PUT', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ suppliers: vis, draftSuppliers:draftVis, expectedVersion:bom.__v }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Save failed');
      setBom(b => ({ ...b, visibility: data.bom.visibility, __v:data.bom.__v ?? b.__v }));
    } catch(err) { setError(err.message); } finally { setVisSaving(false); }
  };
  const toggleSupplier = s => setVis(cur => cur.includes(s) ? cur.filter(x=>x!==s) : [...cur, s]);

  const del = async () => {
    if (!window.confirm(`Delete ${bom.documentNumber}? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/boms/${id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
      const data = await res.json();
      if (data.ok) navigate('/quality/boms');
    } catch {}
  };

  const openSupersede = () => {
    setSupItems(items.map(it => ({ ...it }))); // pre-fill with current items
    setSupName(name); setSupError(''); setSupFileName(''); setSuperseding(true);
  };

  const doSupersede = async () => {
    if (supItems.length === 0) { setSupError('The new BOM needs at least one item'); return; }
    setSupSaving(true); setSupError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/boms/${id}/supersede`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({
          name: supName || name,
          productFamily,
          productNumbers: productNumbersText.split(/[\n,]/).map(s=>s.trim()).filter(Boolean),
          items: supItems,
          sourceFileName: supFileName,
          rawHeaders: [],
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error||'Supersede failed');
      setSuperseding(false);
      navigate(`/quality/boms/${data.bom._id}`);
    } catch(err) { setSupError(err.message); } finally { setSupSaving(false); }
  };

  if (loading) return <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>Loading…</div>;
  if (!bom) return (
    <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>
      {error || 'BOM not found.'} <button onClick={()=>navigate('/quality/boms')} style={{ background:'none',border:'none',color:ACCENT,cursor:'pointer',fontSize:14 }}>Back to list</button>
    </div>
  );

  // Content is editable only in Draft. A Released BOM must be Revised
  // (new draft revision) before it can change — every change is a revision.
  const editableStatus = bom.currentStatus === 'Draft';
  // Mirror backend supplierCanAccess: a supplier may act on a BOM they created, or one
  // shared with them that has been Released at least once (covers the revise cycle).
  // A supplier can READ any Released BOM, so without this gate the Revise button would
  // appear on Released BOMs not shared with them and the backend would 403.
  const myKey = String(role||'').replace(/^test-/,'');
  const supplierCanAccessThis = supplierKey && (
    bom.createdBy === user?.id
    || ((bom.visibility?.suppliers || []).includes(myKey)
        && ((bom.revisions || []).some(r => r.status === 'Released') || bom.currentStatus === 'Released'))
  );
  const canEditThis = !requestedRevision && !snapshot && (canManage || (supplierKey && supplierCanAccessThis)) && editableStatus;
  const readOnly = !canEditThis;

  const availableActions = requestedRevision || snapshot ? [] : Object.entries(TRANSITIONS)
    .filter(([,t]) => !t.from || t.from === bom.currentStatus)
    .map(([key,t]) => ({ key, label:t.label }))
    .filter(a => canManage
      || (supplierKey && supplierCanAccessThis && (a.key === 'submit' || a.key === 'revise')));

  const inp = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'8px 12px',fontSize:13,width:'100%',boxSizing:'border-box' };
  const cellInp = { ...inp,padding:'5px 8px',fontSize:12,borderRadius:4 };
  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };

  return (
    <div style={{ maxWidth:1100 }}>
      <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:8 }}>
        <button onClick={()=>navigate('/quality/boms')}
          style={{ background:'none',border:'none',color:'var(--ni-text-muted)',cursor:'pointer',fontSize:20,padding:0 }}>←</button>
        <span style={{ fontFamily:'monospace',fontSize:13,color:ACCENT }}>{bom.documentNumber}</span>
        <span style={{ fontFamily:'monospace',fontSize:12,color:'var(--ni-text-muted)' }}>Rev. {bom.currentRevision}</span>
        <StatusBadge status={bom.currentStatus} />
      </div>

      {error && <div style={{ color:'#EC2124',fontSize:13,margin:'8px 0',padding:'8px 12px',background:'#EC212411',borderRadius:6 }}>{error}</div>}

      <div style={{ margin:'12px 0', fontSize:13 }}>
        <label>View revision <select aria-label="BOM revision view" value={requestedRevision} onChange={e => setSearchParams(e.target.value ? { revision:e.target.value } : {})}>
          <option value="">Current saved document</option>
          {availableRevisions.map(r => <option key={r.revision} value={r.revision}>{r.revision} — preserved snapshot</option>)}
          {requestedRevision && !availableRevisions.some(r => r.revision === requestedRevision) && <option value={requestedRevision}>{requestedRevision}</option>}
        </select></label>
        {(requestedRevision || snapshot) && <p>Revision-specific snapshot. This view is read-only.</p>}
      </div>
      {canUseChangeControl(user) && bom.partNumber && <div className="ni-toolbar" style={{ margin: '14px 0', gap: 10, flexWrap: 'wrap' }}><Link className="ni-button" to={`/quality/sample-investigations?partNumber=${encodeURIComponent(bom.partNumber)}`}>Sample investigation history</Link><Link className="ni-button" to={`/quality/sample-investigations/new?partNumber=${encodeURIComponent(bom.partNumber)}&bomId=${encodeURIComponent(id)}`}>Review a sample</Link></div>}

      {/* Action bar */}
      {!requestedRevision && !snapshot && (canManage || (supplierKey && (canEditThis || availableActions.length > 0))) && (
        <div style={{ display:'flex',gap:8,marginBottom:18,flexWrap:'wrap' }}>
          {canEditThis && (
            <button onClick={()=>{ setShowSaveModal(true); setChangeSummary(''); }} disabled={!dirty}
              style={{ background:ACCENT,color:'#000',border:'none',borderRadius:6,
                padding:'8px 18px',fontWeight:700,fontSize:13,cursor:dirty?'pointer':'not-allowed',opacity:dirty?1:0.5 }}>
              Save changes
            </button>
          )}
          {availableActions.map(a=>(
            <button key={a.key} onClick={()=>{ setActionModal(a.key); setActionNote(''); }}
              disabled={a.key === 'approve' && !!bom.partNumber && (bom.openPoints || []).some(p => p.status !== 'resolved')}
              title={a.key === 'approve' && !!bom.partNumber && (bom.openPoints || []).some(p => p.status !== 'resolved') ? 'Resolve the open points before release.' : undefined}
              style={{ background:'rgba(255,255,255,0.05)',border:'1px solid var(--ni-border-soft)',
                color:'var(--ni-text)',borderRadius:6,padding:'8px 16px',fontSize:13,fontWeight:600,cursor:'pointer' }}>
              {a.label}
            </button>
          ))}
          {canManage && !bom.partNumber && bom.currentStatus === 'Released' && (
            <button onClick={openSupersede}
              style={{ background:'transparent',border:`1px solid ${ACCENT}66`,color:ACCENT,borderRadius:6,
                padding:'8px 16px',fontWeight:700,fontSize:13,cursor:'pointer' }}>
              Supersede with new BOM
            </button>
          )}
          {canManage && (
            <button onClick={del}
              style={{ background:'rgba(236,33,36,0.12)',border:'none',color:'#EC2124',borderRadius:6,
                padding:'8px 16px',fontSize:13,cursor:'pointer',marginLeft:'auto' }}>Delete</button>
          )}
        </div>
      )}

      {bom.currentStatus === 'Released' && (
        <div style={{ background:'#46BB8915',border:'1px solid #46BB8944',borderRadius:8,
          padding:'10px 14px',fontSize:13,color:'#46BB89',marginBottom:18 }}>
          This BOM is released and read-only. Use <strong>Revise / New Draft</strong> to open a new revision before changing it.
        </div>
      )}
      {(bom.currentStatus === 'Superseded' || bom.currentStatus === 'Obsolete') && (
        <div style={{ background:'#f59e0b15',border:'1px solid #f59e0b44',borderRadius:8,
          padding:'10px 14px',fontSize:13,color:'#f59e0b',marginBottom:18 }}>
          This BOM is {bom.currentStatus.toLowerCase()} and read-only.
        </div>
      )}

      {/* Header metadata */}
      <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:16,marginBottom:16 }}>
        <div><label style={labelS}>Name</label>
          <input value={name} onChange={e=>{ setName(e.target.value); setDirty(true); }} readOnly={readOnly}
            style={{ ...inp,...(readOnly?{ opacity:0.7,cursor:'not-allowed' }:{}) }} /></div>
        <div><label style={labelS}>Product Family</label>
          <input value={productFamily} onChange={e=>{ setProductFamily(e.target.value); setDirty(true); }} readOnly={readOnly}
            style={{ ...inp,...(readOnly?{ opacity:0.7,cursor:'not-allowed' }:{}) }} /></div>
      </div>
      <div style={{ marginBottom:20 }}>
        <label style={labelS}>Product Numbers (one per line or comma-separated)</label>
        <textarea value={productNumbersText} onChange={e=>{ setProductNumbersText(e.target.value); setDirty(true); }}
          readOnly={readOnly} rows={2}
          style={{ ...inp,resize:'vertical',...(readOnly?{ opacity:0.7,cursor:'not-allowed' }:{}) }} />
      </div>

      {/* Supersedes / superseded links */}
      {(bom.supersedesId || bom.supersededById) && (
        <div style={{ display:'flex',gap:10,flexWrap:'wrap',marginBottom:18 }}>
          {bom.supersedesId && (
            <button onClick={()=>navigate(`/quality/boms/${bom.supersedesId}`)}
              style={{ background:'transparent',border:`1px solid ${ACCENT}55`,color:ACCENT,borderRadius:6,
                padding:'5px 12px',fontSize:12,cursor:'pointer' }}>← Supersedes previous BOM</button>
          )}
          {bom.supersededById && (
            <button onClick={()=>navigate(`/quality/boms/${bom.supersededById}`)}
              style={{ background:'transparent',border:`1px solid ${ACCENT}55`,color:ACCENT,borderRadius:6,
                padding:'5px 12px',fontSize:12,cursor:'pointer' }}>Superseded by newer BOM →</button>
          )}
        </div>
      )}

      {/* Items table */}
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
        <label style={labelS}>Items ({items.length})</label>
        {!readOnly && (
          <button onClick={addItem}
            style={{ background:ACCENT+'22',color:ACCENT,border:`1px solid ${ACCENT}44`,borderRadius:6,
              padding:'4px 14px',fontSize:12,fontWeight:700,cursor:'pointer' }}>+ Add item</button>
        )}
      </div>
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,overflow:'auto' }}>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
          <thead>
            <tr style={{ color:'var(--ni-text-muted)',fontSize:11,textTransform:'uppercase',
              letterSpacing:'0.06em',background:'rgba(0,0,0,0.2)' }}>
              {['Item No.','Part Number','REV','Description','Qty','Unit','MFG P/N','MFG','Extras'].map(h=>(
                <th key={h} style={{ padding:'8px 12px',textAlign:'left',fontWeight:600,whiteSpace:'nowrap' }}>{h}</th>
              ))}
              {!readOnly && <th style={{ padding:'8px 12px' }}></th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={readOnly?9:10} style={{ padding:24,textAlign:'center',color:'var(--ni-text-muted)' }}>
                No items.{!readOnly && <> Click <button onClick={addItem} style={{ background:'none',border:'none',color:ACCENT,cursor:'pointer',fontSize:13 }}>+ Add item</button>.</>}
              </td></tr>
            ) : items.map((it, i) => {
              const extras = it.attributes || [];
              const extrasCell = (
                <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)',fontSize:11 }}
                  title={extras.map(a=>`${a.key}: ${a.value}`).join(' · ')}>
                  {extras.length ? `${extras.length} extra${extras.length!==1?'s':''}` : '—'}
                </td>
              );
              return readOnly ? (
                <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                  <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>{it.itemNumber||'—'}</td>
                  <td style={{ padding:'7px 12px',fontFamily:'monospace',color:ACCENT,whiteSpace:'nowrap' }}>{it.ninPartNumber||'—'}{canUseChangeControl(user) && it.ninPartNumber && <><br /><Link style={{ fontFamily: 'inherit', fontSize: 11 }} to={`/quality/sample-investigations?partNumber=${encodeURIComponent(it.ninPartNumber)}`}>Sample history</Link></>}</td>
                  <td style={{ padding:'7px 12px',fontFamily:'monospace',whiteSpace:'nowrap' }}>{it.revision||'—'}</td>
                  <td style={{ padding:'7px 12px' }}>{it.description||'—'}</td>
                  <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)' }}>{it.quantity==null?'—':it.quantity}</td>
                  <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)' }}>{it.unit}</td>
                  <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)',fontFamily:'monospace' }}>{it.manufacturerPartNumber||'—'}</td>
                  <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)' }}>{it.manufacturer||'—'}</td>
                  {extrasCell}
                </tr>
              ) : (
                <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                  <td style={{ padding:'4px 8px' }}><input value={it.itemNumber||''} onChange={e=>setItem(i,'itemNumber',e.target.value)} style={{ ...cellInp,width:64 }} /></td>
                  <td style={{ padding:'4px 8px' }}><input value={it.ninPartNumber} onChange={e=>setItem(i,'ninPartNumber',e.target.value)} style={{ ...cellInp,fontFamily:'monospace',minWidth:110 }} /></td>
                  <td style={{ padding:'4px 8px' }}><input aria-label={`Item ${i+1} revision`} value={it.revision||''} onChange={e=>setItem(i,'revision',e.target.value)} style={{ ...cellInp,fontFamily:'monospace',width:64 }} /></td>
                  <td style={{ padding:'4px 8px' }}><input value={it.description} onChange={e=>setItem(i,'description',e.target.value)} style={{ ...cellInp,minWidth:160 }} /></td>
                  <td style={{ padding:'4px 8px' }}><input type="number" value={it.quantity==null?'':it.quantity} onChange={e=>setItem(i,'quantity',e.target.value)} style={{ ...cellInp,width:64 }} /></td>
                  <td style={{ padding:'4px 8px' }}><input value={it.unit} onChange={e=>setItem(i,'unit',e.target.value)} style={{ ...cellInp,width:64 }} /></td>
                  <td style={{ padding:'4px 8px' }}><input value={it.manufacturerPartNumber} onChange={e=>setItem(i,'manufacturerPartNumber',e.target.value)} style={{ ...cellInp,fontFamily:'monospace',minWidth:110 }} /></td>
                  <td style={{ padding:'4px 8px' }}><input value={it.manufacturer} onChange={e=>setItem(i,'manufacturer',e.target.value)} style={{ ...cellInp,minWidth:110 }} /></td>
                  {extrasCell}
                  <td style={{ padding:'4px 8px' }}>
                    <button onClick={()=>removeItem(i)}
                      style={{ background:'none',border:'none',color:'#EC2124',cursor:'pointer',fontSize:16,padding:'0 4px' }}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop:18 }}><BomControlledDetails key={`${id}:${requestedRevision}`} bom={bom} items={items} availableRevisions={availableRevisions} requestedRevision={requestedRevision || snapshot?.revision || ''} editing={!readOnly} dirty={dirty}
        onChange={(key,value) => { setBom(b => ({ ...b, [key]:value })); setDirty(true); }} onItemChange={setItem} /></div>

      {/* Supplier visibility (NI-controlled, independent of the content revision) */}
      {canManage && !requestedRevision && !snapshot && (
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginTop:18 }}>
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
            <label style={labelS}>Supplier Visibility</label>
            <button onClick={saveVisibility} disabled={visSaving}
              style={{ background:ACCENT+'22',color:ACCENT,border:`1px solid ${ACCENT}44`,borderRadius:6,
                padding:'4px 14px',fontSize:12,fontWeight:700,cursor:visSaving?'wait':'pointer' }}>
              {visSaving?'Saving…':'Save visibility'}
            </button>
          </div>
          <div style={{ fontSize:12,color:'var(--ni-text-muted)',marginBottom:10 }}>
            Suppliers selected here can read a released BOM and open a revision of it for your review.
          </div>
          <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
            {SUPPLIERS.map(s=>(
              <button type="button" key={s} onClick={()=>toggleSupplier(s)}
                style={{ padding:'5px 14px',borderRadius:20,fontSize:12,fontWeight:700,cursor:'pointer',border:'1px solid',
                  background:vis.includes(s)?ACCENT+'22':'transparent',
                  color:vis.includes(s)?ACCENT:'var(--ni-text-muted)',
                  borderColor:vis.includes(s)?ACCENT:'var(--ni-border-soft)' }}>
                {s.toUpperCase()}
              </button>
            ))}
          </div>
          <p style={{ fontSize:12, color:'var(--ni-text-muted)' }}>Draft review access is read-only and does not release this BOM.</p>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            {SUPPLIERS.map(s => <label key={s} style={{ fontSize:12 }}><input type="checkbox"
              aria-label={`${s} draft review`} checked={draftVis.includes(s)}
              onChange={() => setDraftVis(cur => cur.includes(s) ? cur.filter(v => v !== s) : [...cur,s])} /> {s.toUpperCase()} draft review</label>)}
          </div>
        </div>
      )}

      {/* Revision history */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginTop:18 }}>
        <div style={labelS}>Revision History</div>
        {(bom.revisions||[]).length === 0 ? (
          <div style={{ fontSize:13,color:'var(--ni-text-muted)',marginTop:6 }}>No revisions recorded yet.</div>
        ) : (
          <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12,marginTop:8 }}>
            <thead>
              <tr style={{ color:'var(--ni-text-muted)',fontSize:10,textTransform:'uppercase',letterSpacing:'0.06em' }}>
                {['Rev','Status','Summary','By','Date'].map(h=>(
                  <th key={h} style={{ padding:'6px 10px',textAlign:'left',fontWeight:600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...(bom.revisions||[])].reverse().map((r,i)=>(
                <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                  <td style={{ padding:'8px 10px',fontFamily:'monospace' }}>{r.revision}</td>
                  <td style={{ padding:'8px 10px' }}><StatusBadge status={r.status}/></td>
                  <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>{r.changeSummary}</td>
                  <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>{r.updatedByName}</td>
                  <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>
                    {r.updatedAt?new Date(r.updatedAt).toLocaleDateString():'—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Save modal */}
      {showSaveModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-char)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>Save Changes</h3>
            <div style={labelS}>Change Summary</div>
            <input value={changeSummary} onChange={e=>setChangeSummary(e.target.value)}
              placeholder="Describe what changed…" style={{ ...inp,marginBottom:20 }} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={save} disabled={saving}
                style={{ background:ACCENT,color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:saving?'wait':'pointer' }}>
                {saving?'Saving…':'Save revision'}
              </button>
              <button onClick={()=>setShowSaveModal(false)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-char)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>{TRANSITIONS[actionModal]?.label}</h3>
            <div style={labelS}>Note (optional)</div>
            <input value={actionNote} onChange={e=>setActionNote(e.target.value)}
              placeholder="Reason or comment…" style={{ ...inp,marginBottom:20 }} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={()=>handleAction(actionModal)} disabled={saving}
                style={{ background:ACCENT,color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:saving?'wait':'pointer' }}>
                {saving?'Processing…':'Confirm'}
              </button>
              <button onClick={()=>setActionModal(null)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Supersede modal */}
      {superseding && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:1000,padding:16 }}>
          <div style={{ background:'var(--ni-char)',border:'1px solid var(--ni-border-soft)',borderRadius:12,
            padding:24,width:920,maxWidth:'100%',maxHeight:'90vh',overflowY:'auto' }}>
            <h3 style={{ margin:'0 0 6px',fontSize:16 }}>Supersede {bom.documentNumber}</h3>
            <p style={{ margin:'0 0 16px',color:'var(--ni-text-muted)',fontSize:13 }}>
              Creates a new BOM (next number, revision bumped) and marks this one Superseded.
              The current items are pre-loaded — re-import to replace them.
            </p>
            <div style={{ marginBottom:14 }}>
              <label style={labelS}>New BOM name</label>
              <input value={supName} onChange={e=>setSupName(e.target.value)} style={inp} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={labelS}>Re-import items (optional — overwrites the pre-loaded list)</label>
              <BomImport onItems={setSupItems} onFileName={setSupFileName} />
            </div>
            <div style={{ color:'var(--ni-text-muted)',fontSize:12,marginBottom:14 }}>
              {supItems.length} item{supItems.length!==1?'s':''} will be saved to the new BOM.
            </div>
            {supError && <div style={{ color:'#EC2124',fontSize:13,marginBottom:12 }}>{supError}</div>}
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={doSupersede} disabled={supSaving}
                style={{ background:ACCENT,color:'#000',border:'none',borderRadius:6,
                  padding:'9px 20px',fontWeight:700,fontSize:13,cursor:supSaving?'wait':'pointer' }}>
                {supSaving?'Creating…':'Create superseding BOM'}
              </button>
              <button onClick={()=>setSuperseding(false)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
                  borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
